import { useState, useRef } from 'react';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import { SearchOutlined, Close } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: number | string;
  sx?: React.CSSProperties;
  onExpandChange?: (isExpanded: boolean) => void;
}

export const SearchField = ({
  value,
  onChange,
  placeholder,
  width = 300,
  sx = {},
  onExpandChange,
}: SearchFieldProps) => {
  const t = useTranslations();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
    onExpandChange?.(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSearchCollapse = () => {
    setIsSearchExpanded(false);
    onExpandChange?.(false);
    onChange('');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (isSearchExpanded) {
        handleSearchCollapse();
      } else {
        onChange('');
      }
    }
  };

  if (isSearchExpanded) {
    return (
      <TextField
        ref={searchInputRef}
        variant="standard"
        size="small"
        placeholder={placeholder || t('common.search')}
        value={value}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        autoFocus
        sx={{
          width: '100%',
          minWidth: '300px',
          ...sx,
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearchCollapse} size="small" sx={{ color: 'text.secondary' }}>
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    );
  }

  return (
    <>
      {/* Desktop: persistent search field */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, width, ...sx }}>
        <TextField
          variant="standard"
          size="small"
          placeholder={placeholder || t('common.search')}
          value={value}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => onChange('')}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    padding: '2px',
                    minWidth: '20px',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  <Close sx={{ fontSize: '16px' }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      {/* Mobile: icon to expand search */}
      <IconButton
        size="small"
        onClick={handleSearchExpand}
        sx={{ color: 'text.secondary', display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0, ...sx }}
      >
        <SearchOutlined />
      </IconButton>
    </>
  );
};
